import {useFormContext} from 'platform/components';

import {useState} from 'react';
import {Controller} from 'react-hook-form';

import {sort} from 'ramda';

import {TestIdProps} from 'shared/web';

import {useCatalogue} from '../../../../../hooks/useCatalogue';
import {EMPTY_PRESET_FEATURES} from '../consts/emptyPresetFeatures';
import {normalizePresetFeatures} from '../utils/normalizePresetFeatures';
import {PresetEditorFormValues} from '../utils/presetEditorFormSchema';
import {PresetFeaturesFieldBody} from './PresetFeaturesFieldBody';

interface PresetFeaturesFieldProps extends TestIdProps {
  isReadOnly?: boolean;
}

export function PresetFeaturesField(props: PresetFeaturesFieldProps) {
  const {isReadOnly} = props;
  const {control} = useFormContext<PresetEditorFormValues>();
  const {getFeatureLabel, hasFeatureTranslation, isLoading} = useCatalogue();
  const [isOpen, setIsOpen] = useState(false);
  const [draftValue, setDraftValue] = useState<string[]>(EMPTY_PRESET_FEATURES);

  return (
    <Controller
      name="features"
      control={control}
      render={({field}) => {
        const features =
          normalizePresetFeatures(field.value, hasFeatureTranslation) ?? EMPTY_PRESET_FEATURES;

        const sortedFeatureItems = sort(
          (left, right) => getFeatureLabel(left).localeCompare(getFeatureLabel(right)),
          features
        ).map((featureKey) => ({key: featureKey, label: getFeatureLabel(featureKey)}));

        const updateFeatures = (value: string[]) => {
          field.onChange(normalizePresetFeatures(value, hasFeatureTranslation));
        };

        const openDialog = () => {
          if (isLoading || isReadOnly) {
            return;
          }

          setDraftValue(features);
          setIsOpen(true);
        };

        const confirmSelection = (value: string[]) => {
          updateFeatures(value);
          setIsOpen(false);
        };

        return (
          <PresetFeaturesFieldBody
            sortedFeatureItems={sortedFeatureItems}
            draftValue={draftValue}
            isOpen={isOpen}
            isLoading={isLoading}
            isReadOnly={isReadOnly}
            onDraftChange={setDraftValue}
            onOpen={openDialog}
            onClose={() => setIsOpen(false)}
            onConfirm={confirmSelection}
            onChange={updateFeatures}
            data-testid={props['data-testid']}
          />
        );
      }}
    />
  );
}
